import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, Tag, Plus, ArrowRight, Loader2, BarChart3, Users, Eye } from 'lucide-react';
import axios from 'axios';
import { PageMeta } from '@/components/PageMeta';
import { API } from '@/lib/api';
import { ContactRequestsList } from '@/components/admin/ContactRequestsList';


export default function AdminDashboardPage() {
  const [postsCount, setPostsCount] = useState(null);
  const [plansCount, setPlansCount] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [posts, pricing, summary] = await Promise.all([
          axios.get(`${API}/blog/admin/posts`),
          axios.get(`${API}/pricing`),
          axios.get(`${API}/analytics/summary`),
        ]);
        setPostsCount(posts.data.length);
        setPlansCount(pricing.data.plans?.filter((p) => p.active).length || 0);
        setAnalytics(summary.data);
      } catch {
        setPostsCount(0);
        setPlansCount(0);
        setAnalytics(null);
      }
    };
    load();
  }, []);

  return (
    <AdminLayout>
      <PageMeta title="Tableau de bord admin" description="Back-office CrossFit Saint-Chamond" />
      <div className="mb-8">
        <span className="text-primary font-semibold text-xs tracking-[0.3em] uppercase">━━ Back-office</span>
        <h1 className="font-heading text-4xl md:text-5xl text-dark mt-2 leading-tight">
          BIENVENUE <span className="text-gradient-primary italic font-display inline-block pr-2">NICOLAS</span>
        </h1>
        <p className="text-muted-foreground mt-2">Gère les contenus de ton site depuis cet espace.</p>
      </div>

      {/* Analytics snapshot */}
      <Card className="p-6 rounded-2xl bg-gradient-to-br from-primary via-primary-dark to-dark text-white mb-6 overflow-hidden relative" data-testid="dashboard-analytics-snapshot">
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-primary-glow/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10">
          <div className="flex items-start justify-between mb-5">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1">Aperçu rapide</div>
              <h2 className="font-heading text-xl tracking-wide">CES 7 DERNIERS JOURS</h2>
            </div>
            <Link to="/admin/analytics">
              <Button variant="outline" size="sm" className="border-white/20 bg-white/5 text-white hover:bg-white/10 rounded-xl">
                Voir les analytics <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="flex items-center gap-1.5 text-[10px] text-white/60 tracking-widest uppercase mb-1.5">
                <Users className="h-3 w-3" /> Visiteurs
              </div>
              <div className="font-display text-3xl">
                {analytics === null ? <Loader2 className="h-5 w-5 animate-spin" /> : (analytics?.visitors?.week ?? 0).toLocaleString('fr-FR')}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-1.5 text-[10px] text-white/60 tracking-widest uppercase mb-1.5">
                <Eye className="h-3 w-3" /> Pages vues
              </div>
              <div className="font-display text-3xl">
                {analytics === null ? <Loader2 className="h-5 w-5 animate-spin" /> : (analytics?.pageviews?.week ?? 0).toLocaleString('fr-FR')}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-1.5 text-[10px] text-white/60 tracking-widest uppercase mb-1.5">
                <BarChart3 className="h-3 w-3" /> Contacts
              </div>
              <div className="font-display text-3xl">
                {analytics === null ? <Loader2 className="h-5 w-5 animate-spin" /> : (analytics?.contacts_30d ?? 0).toLocaleString('fr-FR')}
              </div>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-5 mb-10">
        <Card className="p-6 rounded-2xl border-border hover:border-primary/40 transition-all hover:shadow-lg" data-testid="dashboard-card-blog">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <div className="text-muted-foreground text-xs tracking-widest uppercase">Articles</div>
              <div className="font-display text-4xl text-dark leading-none mt-1">
                {postsCount === null ? <Loader2 className="h-6 w-6 animate-spin" /> : postsCount}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/blog" className="flex-1">
              <Button variant="outline" className="w-full rounded-xl">Gérer</Button>
            </Link>
            <Link to="/admin/blog/new">
              <Button className="bg-primary hover:bg-primary-light rounded-xl" data-testid="dashboard-new-post">
                <Plus className="h-4 w-4 mr-1" /> Nouveau
              </Button>
            </Link>
          </div>
        </Card>

        <Card className="p-6 rounded-2xl border-border hover:border-primary/40 transition-all hover:shadow-lg" data-testid="dashboard-card-pricing">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Tag className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <div className="text-muted-foreground text-xs tracking-widest uppercase">Plans actifs</div>
              <div className="font-display text-4xl text-dark leading-none mt-1">
                {plansCount === null ? <Loader2 className="h-6 w-6 animate-spin" /> : plansCount}
              </div>
            </div>
          </div>
          <Link to="/admin/pricing">
            <Button className="w-full bg-primary hover:bg-primary-light rounded-xl group">
              Modifier les tarifs
              <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </Card>
      </div>

      {/* New contact requests */}
      <Card className="p-6 rounded-2xl border-border mb-6" data-testid="dashboard-contact-requests">
        <ContactRequestsList onlyNew={true} limit={20} title="📬 Nouvelles demandes" />
      </Card>
    </AdminLayout>
  );
}
